import {
	isLogged,
	isSuplanting,
	loggedUserId,
	suplantingUserId
} from '@contexts/auth/infrastructure';
import {
	booleanValueObject,
	Clock,
	enumValueObject,
	type FindAllResponse,
	stringValueObject,
	type UseCaseCriteria,
	uuidValueObject
} from '@contexts/shared/domain';
import { findAllEntities } from '@contexts/shared/infrastructure';
import {
	type Language,
	LanguageOptions,
	type Role,
	RoleOptions,
	type Status,
	StatusOptions,
	type UpdateApiKeyAccessInput,
	type UpdateLastAlertsCheckInput,
	type UpdateUserInput,
	type User,
	type UserCore,
	type UserRepository
} from '@contexts/users/domain';

export const DEMO_CLIENT_USER_ID = 'c1e1e000-0000-4000-8000-000000000001';

export const users: User[] = [
	generateValidUser({
		id: '6640acbb-5386-49ef-9dd4-4603dd16f031',
		role: RoleOptions.ADMIN,
		hasApiKey: true
	}),
	...generateUsers()
];

export function inMemoryUserRepositoryBuilder(): UserRepository {
	return {
		create: async function create(user: UserCore): Promise<User> {
			if (users.find((storedUser) => storedUser.id === user.id)) {
				throw new Error('User already exists');
			}

			const newUser = {
				...user,
				hasApiKey: false,
				status: StatusOptions.NOT_VERIFIED,
				createdAt: Clock().getNow(),
				updatedAt: Clock().getNow()
			};

			users.push(newUser);

			return newUser;
		},
		update: async function update(user: UpdateUserInput): Promise<User> {
			const userIndex = users.findIndex((storedUser) => storedUser.id === user.id);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			users[userIndex] = {
				...users[userIndex],
				...user,
				updatedAt: Clock().getNow()
			};

			return users[userIndex];
		},
		updateApiKeyAccess: async function updateApiKeyAccess(
			input: UpdateApiKeyAccessInput
		): Promise<User> {
			const userIndex = users.findIndex((storedUser) => storedUser.id === input.id);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			users[userIndex] = {
				...users[userIndex],
				hasApiKey: input.hasApiKey,
				updatedAt: Clock().getNow()
			};

			return users[userIndex];
		},
		updateLastAlertsCheck: async function updateLastAlertsCheck(
			input: UpdateLastAlertsCheckInput
		): Promise<User> {
			const userIndex = users.findIndex((storedUser) => storedUser.id === input.id);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			users[userIndex] = {
				...users[userIndex],
				lastAlertsCheck: input.lastAlertsCheck,
				updatedAt: Clock().getNow()
			};

			return users[userIndex];
		},
		findAll: async function findAllUsers(
			criteria: UseCaseCriteria
		): Promise<FindAllResponse<User>> {
			return findAllEntities(users, criteria);
		},
		delete: async function deleteUser(id: string): Promise<string> {
			const userIndex = users.findIndex((storedUser) => storedUser.id === id);

			if (userIndex === -1) {
				throw new Error('User not found');
			}

			users.splice(userIndex, 1);

			return id;
		},
		getLoggedUser: async function getLoggedUser(): Promise<User> {
			if (isLogged === false) {
				throw new Error('User not logged');
			}
			const suplantUser =
				isSuplanting && suplantingUserId
					? users.find((storedUser) => storedUser.id === suplantingUserId)
					: null;
			const loggedUser = loggedUserId
				? users.find((storedUser) => storedUser.id === loggedUserId)
				: null;
			const baseUser = suplantUser ?? loggedUser ?? users[0];

			return { ...baseUser, suplant: isSuplanting };
		},
		resendActivationCode: async function resendActivationCode(id: string): Promise<void> {}
	};
}

function generateUsers(): User[] {
	return [
		generateValidUser({
			firstName: 'Test',
			lastName: 'User',
			email: 'test.user@neki.es',
			role: RoleOptions.DELEGATION_MANAGER,
			status: StatusOptions.ACTIVE,
			id: '53f9753d-786b-4918-bae8-0b29d4ec2aa6'
		}),
		generateValidUser({
			firstName: 'Carlos',
			lastName: 'Díaz',
			email: 'carlos.diaz@neki.es',
			role: RoleOptions.ROOM_STAFF,
			status: StatusOptions.BLOCKED
		}),
		generateValidUser({
			firstName: 'Marta',
			lastName: 'López',
			email: 'marta.lopez@neki.es',
			role: RoleOptions.OPERATOR,
			status: StatusOptions.NOT_VERIFIED
		}),
		generateValidUser({
			firstName: 'Isabel',
			lastName: 'Fernández',
			email: 'isabel.fernandez@neki.es',
			role: RoleOptions.ROOM_STAFF,
			status: StatusOptions.BLOCKED
		}),
		generateValidUser({
			firstName: 'Manuel',
			lastName: 'Castillo',
			email: 'manuel.castillo@neki.es',
			role: RoleOptions.OPERATOR,
			status: StatusOptions.BLOCKED
		}),
		generateValidUser({
			firstName: 'Laura',
			lastName: 'Romero',
			email: 'laura.romero@neki.es',
			role: RoleOptions.ROOM_STAFF,
			status: StatusOptions.NOT_VERIFIED
		}),
		generateValidUser({
			firstName: 'Andrés',
			lastName: 'Navarro',
			email: 'andres.navarro@neki.es',
			role: RoleOptions.OPERATOR,
			status: StatusOptions.NOT_VERIFIED
		}),
		generateValidUser({
			firstName: 'Elena',
			lastName: 'Ruiz',
			email: 'elena.ruiz@neki.es',
			role: RoleOptions.OPERATOR,
			status: StatusOptions.ACTIVE,
			language: LanguageOptions.ES
		}),
		generateValidUser({
			firstName: 'Lucía',
			lastName: 'Prieto',
			email: 'lucia.prieto@neki.es',
			role: RoleOptions.ROOM_STAFF,
			status: StatusOptions.ACTIVE,
			language: LanguageOptions.ES
		}),
		generateValidUser({
			firstName: 'Diego',
			lastName: 'Cano',
			email: 'diego.cano@neki.es',
			role: RoleOptions.OPERATOR,
			status: StatusOptions.NOT_VERIFIED
		}),
		generateValidUser({
			firstName: 'Raúl',
			lastName: 'Giménez',
			email: 'raul.gimenez@neki.es',
			role: RoleOptions.OPERATOR,
			status: StatusOptions.ACTIVE
		}),
		generateValidUser({
			firstName: 'Patricia',
			lastName: 'Vega',
			email: 'patricia.vega@neki.es',
			role: RoleOptions.ROOM_STAFF,
			status: StatusOptions.NOT_VERIFIED
		}),
		generateValidUser({
			firstName: 'Clara',
			lastName: 'Cliente',
			email: 'clara.cliente@neki.es',
			role: RoleOptions.CLIENT,
			status: StatusOptions.ACTIVE,
			id: DEMO_CLIENT_USER_ID
		})
	];
}

export function generateValidUser({
	email = 'admin@neki.com',
	role = RoleOptions.ADMIN,
	firstName = 'John',
	lastName = 'Doe',
	delegationId = '01a3490d-cbd4-4b15-aa71-8a12fa824cd0',
	language = LanguageOptions.ES,
	status = StatusOptions.NOT_VERIFIED,
	id = uuidValueObject().generate(),
	hasApiKey = false
}): User {
	return {
		id,
		firstName: stringValueObject().validate('firstName', firstName),
		lastName: stringValueObject().validate('lastName', lastName),
		email: stringValueObject().validate('email', email),
		role: enumValueObject<RoleOptions, Role>().setEnum(RoleOptions).validate('role', role),
		delegationId: uuidValueObject().validateOptional('delegationId', delegationId),
		language: enumValueObject<LanguageOptions, Language>()
			.setEnum(LanguageOptions)
			.validate('language', language),
		phone: stringValueObject().validate('phone', '+34666666666'),
		status: enumValueObject<StatusOptions, Status>()
			.setEnum(StatusOptions)
			.validate('status', status),
		hasApiKey: booleanValueObject().validate('hasApiKey', hasApiKey),
		createdAt: Clock().getNow(),
		updatedAt: Clock().getNow(),
		lastLogin: Clock().getNow(),
		currentLogin: Clock().getNow()
	};
}
